import { Link } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';

interface Service {
  id: string;
  title: string;
  desc: string;
  icon: string;
  image?: string;
  tag?: string;
}

interface ServicesOverviewProps {
  heading?: string;
  subheading?: string;
  services?: Service[];
  showCTA?: boolean;
}

const defaultServices: Service[] = [
  {
    id: 'automotive-tinting',
    title: 'Automotive Tinting',
    desc: 'Professional window tinting for all vehicles — cars, SUVs, and trucks. Superior heat rejection, UV blockage, privacy, and a clean finished look with premium film.',
    icon: 'ri-car-fill',
    tag: 'Most Popular',
    image: 'https://public.readdy.ai/ai/img_res/d88ca2bfdfad07522cd71fb1b44b37db.jpg',
  },
  {
    id: 'commercial-tinting',
    title: 'Commercial Tinting',
    desc: 'High-performance window film for offices, storefronts, and commercial buildings — reducing glare, lowering energy costs, and enhancing building aesthetics.',
    icon: 'ri-building-line',
    image: 'https://readdy.ai/api/search-image?query=commercial%20building%20window%20tinting%20professional%20film%20application%20modern%20office%20glass%20storefront%20exterior%20dramatic%20lighting%20urban%20architecture%20privacy%20film%20premium%20tint%20installation%20Philadelphia%20commercial&width=600&height=500&seq=home-svc-comm-001&orientation=landscape',
  },
  {
    id: 'residential-tinting',
    title: 'Residential Tinting',
    desc: 'Window film solutions for homes — block UV rays, reduce heat, protect furnishings, and enhance privacy without sacrificing natural light.',
    icon: 'ri-home-4-line',
    image: 'https://readdy.ai/api/search-image?query=residential%20home%20window%20tinting%20film%20installation%20modern%20house%20interior%20natural%20light%20privacy%20UV%20protection%20sunlight%20reduction%20professional%20window%20film%20application%20clean%20minimal%20interior&width=600&height=500&seq=home-svc-res-001&orientation=landscape',
  },
  {
    id: 'ceramic-tinting',
    title: 'Ceramic Tinting',
    desc: 'Top-tier ceramic window film with maximum heat rejection and clarity — no signal interference, no fading, and long-lasting performance for demanding drivers.',
    icon: 'ri-contrast-drop-2-line',
    image: 'https://readdy.ai/api/search-image?query=ceramic%20window%20tint%20film%20close%20up%20luxury%20car%20window%20premium%20ceramic%20tinting%20dark%20sleek%20dramatic%20cinematic%20studio%20dark%20background%20high%20contrast%20moody%20automotive%20detail%20professional%20installation&width=600&height=500&seq=home-svc-cer-001&orientation=landscape',
  },
];

export default function ServicesOverview({
  heading = 'Professional Tinting Services',
  subheading = 'Premium window film solutions for automotive, residential, and commercial applications.',
  services = defaultServices,
  showCTA = true,
}: ServicesOverviewProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const [featured, ...rest] = services;

  return (
    <section className="bg-[#080808] py-28 md:py-36 px-6 md:px-12 lg:px-16" ref={ref}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div
          className={`flex flex-col lg:flex-row lg:items-end justify-between mb-16 gap-6 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-[1px] bg-[#AAFF00]" />
              <span className="text-[#AAFF00] text-[11px] font-sans font-medium tracking-[0.3em] uppercase">Our Services</span>
            </div>
            <h2 className="font-serif text-4xl md:text-5xl xl:text-6xl font-bold text-white leading-[1.05]">
              {heading}
            </h2>
          </div>
          <p className="text-white/50 font-sans text-base leading-relaxed max-w-sm lg:max-w-xs lg:text-right">
            {subheading}
          </p>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* Featured large */}
          <div
            className={`lg:col-span-7 relative group overflow-hidden rounded-2xl transition-all duration-700 delay-100 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div className="h-[520px] overflow-hidden">
              <img
                src={featured.image}
                alt={featured.title}
                className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-[#080808] via-[#080808]/25 to-transparent" />
            {featured.tag && (
              <div className="absolute top-6 right-6">
                <span className="bg-[#AAFF00] text-[#080808] text-[11px] font-sans font-semibold px-3 py-1.5 rounded-full">
                  {featured.tag}
                </span>
              </div>
            )}
            <div className="absolute bottom-0 left-0 p-8 md:p-10">
              <div className="w-10 h-10 flex items-center justify-center mb-4 bg-[#AAFF00]/10 rounded-xl border border-[#AAFF00]/20">
                <i className={`${featured.icon} text-[#AAFF00] text-xl`}></i>
              </div>
              <h3 className="font-serif text-white text-3xl font-bold mb-3">{featured.title}</h3>
              <p className="text-white/60 text-sm font-sans leading-relaxed max-w-sm">{featured.desc}</p>
            </div>
          </div>

          {/* Right stacked cards */}
          <div className="lg:col-span-5 grid grid-rows-3 gap-4">
            {rest.map((svc, i) => (
              <div
                key={svc.id}
                className={`relative group overflow-hidden rounded-2xl transition-all duration-700 ${
                  visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: `${(i + 2) * 100}ms` }}
              >
                <div className="h-[153px] overflow-hidden">
                  <img
                    src={svc.image}
                    alt={svc.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-r from-[#080808]/95 via-[#080808]/65 to-transparent" />
                <div className="absolute inset-0 flex items-center gap-5 px-6">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#AAFF00]/10 border border-[#AAFF00]/20 shrink-0">
                    <i className={`${svc.icon} text-[#AAFF00] text-lg`}></i>
                  </div>
                  <div>
                    <h3 className="font-serif text-white text-xl font-bold mb-1">{svc.title}</h3>
                    <p className="text-white/55 text-xs font-sans leading-relaxed line-clamp-2">{svc.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA strip */}
        {showCTA && (
          <div
            className={`mt-12 flex flex-col sm:flex-row items-center justify-between gap-5 p-8 bg-[#111111] border border-white/8 rounded-2xl transition-all duration-700 delay-500 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <div>
              <p className="text-white font-serif text-xl font-semibold mb-1">Ready to tint your vehicle or property?</p>
              <p className="text-white/45 text-sm font-sans">Every job starts with a free consultation and custom quote.</p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <Link
                to="/services"
                className="whitespace-nowrap flex items-center gap-2 border border-white/15 text-white/70 text-sm font-sans font-medium px-6 py-3 rounded-full hover:border-white/35 hover:text-white transition-all duration-200"
              >
                View All Services
              </Link>
              <a
                href="#estimate"
                className="whitespace-nowrap flex items-center gap-2 bg-[#AAFF00] text-[#080808] text-sm font-sans font-semibold px-6 py-3 rounded-full hover:bg-[#C4FF33] transition-all duration-200 cursor-pointer"
              >
                Get Your Quote
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-sm"></i>
                </div>
              </a>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
